import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Box,
  IconButton,
  Chip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Tooltip,
  Alert,
  Input,
} from "@mui/material";
import DataTable from "react-data-table-component";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Gavel as GavelIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  Campaign as CampaignIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { auctionServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { capitalize } from "../../utils/helpers";
import TableFilter from "../../components/shared/TableFilter";
import { Loader } from "../../components/CustomLoader";
import { toast } from "react-toastify";
import CustomPagination from "../../components/CustomPagination";
import Swal from "sweetalert2";

const AuctionManagementList = () => {
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [auctionStatusFilter, setAuctionStatusFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelReasonDialog, setCancelReasonDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    id: "",
    currentStatus: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    fetchAuctionsData();
  }, [currentPage, pageSize, statusFilter, auctionStatusFilter, typeFilter]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== undefined) {
        setCurrentPage(1); 
        fetchAuctionsData();
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const fetchAuctionsData = async () => {
    try {
      setShowLoader(true);
      setError(null);
      
      const params = {
        pageNumber: currentPage,
        pageSize: pageSize,
        searchTerm,
        status: statusFilter.length > 0 ? statusFilter : undefined,
        auctionStatus: auctionStatusFilter.length > 0 ? auctionStatusFilter : undefined,
        auctionType: typeFilter.length > 0 ? typeFilter : undefined,
      };
      
      const response = await auctionServices.getAllAuctions(params);
      
      // Show loader for minimum 1 second for better UX
      setTimeout(() => {
        if (response?.data?.data?.auctions) {
          setAuctions(response.data.data.auctions);
          setTotalCount(response.data.data.total || 0);
        }
        setShowLoader(false);
      }, 1000);
      
    } catch (error) {
      setError(error.message);
      setShowLoader(false);
      errorHandler(error);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const confirmStatusToggle = async () => {
    const { id, currentStatus } = statusDialog;
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const res = await auctionServices.changeStatus({
        id,
        status: newStatus
      });
      if (res?.status === 200) {
        toast.success(res.data.message || `Auction ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`);
        fetchAuctionsData();
      } else {
        toast.error(res.data.message || 'Failed to update auction status');
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setStatusDialog({ open: false, id: "", currentStatus: 0 });
    }
  };

  const handleDelete = async (auction) => {
    const result = await Swal.fire({
      text: "Are you sure you want to delete this auction? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    });

    if (result.isConfirmed) {
      try {
        const res = await auctionServices.deleteAuction({ id: auction._id });
        if(res?.status === 200){
          toast.success(res?.data?.message, { toastId: "success" });
        }else{
          toast.error(res?.data?.message, { toastId: "error" });
        }
        fetchAuctionsData();
      } catch (error) {
        errorHandler(error);
      }
    }
  };

  const handleCancel = (auction) => {
    setSelectedAuction(auction);
    setCancelDialog(true);
  };

  const handlePromotionalBanner = (auction) => {
    navigate("/banner-management/add", {
      state: {
        auctionId: auction._id,
        auctionTitle: auction?.vehicle?.vehicleName,
      },
    });
  };
  


  const confirmCancel = () => {
    setCancelDialog(false);
    setCancelReasonDialog(true);
  };

  const submitCancelWithReason = async () => {
    if (!cancelReason.trim()) {
      setError("Please provide a reason for cancelling the auction");
      return;
    }
    
    try {
      const response = await auctionServices.cancelAuction({ 
        id: selectedAuction._id, 
        reason: cancelReason.trim() 
      });
      if(response?.status === 200){
        toast.success(response?.data?.message, { toastId: "success" });
      }else{
        toast.error(response?.data?.message, { toastId: "error" });
      }
      setCancelReasonDialog(false);
      setSelectedAuction(null);
      setCancelReason("");
      fetchAuctionsData();
    } catch (error) {
      errorHandler(error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAuctionStatusChip = (auction) => {
    if (!auction) return null;

    if (auction.auctionStatus == "Cancelled") {
      return <Chip label="Cancelled" color="error" size="small" />;
    }
    if (auction.auctionStatus == "Upcoming") {
      return <Chip label="Upcoming" color="info" size="small" />;
    } else if (auction.auctionStatus == "Live") {
      return <Chip label="Live" color="success" size="small" />;
    } else if (auction.auctionStatus == "Paused") {
      return <Chip label="Paused" color="warning" size="small" />;
    } else if (auction.auctionStatus == "Stopped") {
      return <Chip label="Stopped" color="error" size="small" />;
    }else {
      return <Chip label="Completed" color="default" size="small" />;
    }
  };

  const columns = [
    {
      name: "S.No.",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "80px",
      center: true,
    },
    {
      name: "Vehicle Name",
      cell: (row) => (
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">
            {row?.vehicle?.make?.name + " " + row?.vehicle?.model}
          </Typography>
        
        </Box>
      ),
    },
    {
      name: "Event Type",
      cell: (row) => 
        capitalize(row.auctionType)
      ,
      center: true,
    },
    {
      name: "Original Price",
      cell: (row) => (
        <Typography variant="body2" >
          {formatCurrency(row?.vehicle?.originalPrice || 0)}
        </Typography>
      ),
      center: true,
    },
    {
      name: "Current Bid",
      cell: (row) => (
        row.currentBid ? (
          <Typography variant="body2" >
            {formatCurrency(row.currentBid)}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No bids
          </Typography>
        )
      ),
      center: true,
    },
    {
      name: "Start Date",
      cell: (row) => (
        <Typography variant="body2">
          {formatDate(row.schedule?.startTime)}
        </Typography>
      ),
    },
    {
      name: "End Date",
      cell: (row) => (
        <Box>
          <Typography variant="body2">
            {formatDate(row.schedule?.endTime)}
          </Typography>
          {/* <Typography variant="caption" color="text.secondary">
            {getTimeRemaining(row.schedule?.endTime)}
          </Typography> */}
        </Box>
      ),
    },
    {
      name: "Event Status",
      cell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {getAuctionStatusChip(row)}
        </Box>
      ),
      center: true,
    },
    // {
    //   name: "Status",
    //   cell: (row) => (
    //     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
         
    //       <Switch
    //         checked={row.status === 1}
    //         onChange={() => handleStatusToggle(row)}
    //         size="small"
    //       />
    //     </Box>
    //   ),
    //   center: true,
    // },
    {
      name: "Actions",
      cell: (row) => {
        const isDisabled = row.status === 0;
        const isEditDisabled = row.auctionStatus == "Cancelled" || row.auctionStatus == "Completed" || row.auctionStatus == "Live" ;
        const isBannerDisabled = row.auctionStatus == "Cancelled" || row.auctionStatus == "Completed" || row.auctionStatus == "Live";
        const isCancelDisabled = row.auctionStatus == "Cancelled" || row.auctionStatus == "Completed";
        
        return (
          <Box display="flex" gap={1} justifyContent="center">
            <Tooltip title="View">
              <IconButton
                size="small"
                onClick={() => navigate(`/event-management/view/${row._id}`           )}
                sx={{ color: isDisabled ? "grey.400" : "primary.main" }}
              >
                <ViewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                disabled={isEditDisabled || isDisabled}
                size="small"
                onClick={() => navigate(`/event-management/edit/${row._id}`)}
                sx={{ 
                  color: (isEditDisabled || isDisabled) ? "grey.400" : "primary.main",
                  "&.Mui-disabled": {
                    color: "grey.400",
                    opacity: 0.6
                  }
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Create Promotional Banner">
              <IconButton
                disabled={isBannerDisabled || isDisabled}
                size="small"
                onClick={() => handlePromotionalBanner(row)}
                sx={{ 
                  color: (isBannerDisabled || isDisabled) ? "grey.400" : "secondary.main",
                  "&.Mui-disabled": {
                    color: "grey.400",
                    opacity: 0.6
                  }
                }}
              >
                <CampaignIcon />
              </IconButton>
            </Tooltip>
            {!row.isCancelled && (
              <Tooltip title="Cancel">
                <IconButton
                  disabled={isCancelDisabled || isDisabled}
                  size="small"
                  onClick={() => handleCancel(row)}
                  sx={{ 
                    color: (isCancelDisabled || isDisabled) ? "grey.400" : "warning.main",
                    "&.Mui-disabled": {
                      color: "grey.400",
                      opacity: 0.6
                    }
                  }}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDelete(row)}
                sx={{ color: isDisabled ? "grey.400" : "error.main" }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
      width: "200px",
      center: true,
    },
  ];


  return (
    <Card>
      <CardHeader title="Event Management" />
      <Box display="flex" flexWrap="wrap" justifyContent="flex-end" alignItems="center" p={2} gap={2}>
      <TableFilter
          data={[
            { label: "Active", value: 1 },
            { label: "Inactive", value: 0 }
          ]}
          value={statusFilter}
          setValue={setStatusFilter}
          filterName="Status"
        />
        <TableFilter
          data={[
            { label: "Upcoming", value: "upcoming" },
            { label: "Live", value: "live" },
            { label: "Completed", value: "completed" },
            { label: "Cancelled", value: "cancelled" },
            { label: "Paused", value: "paused" },
          ]}
          value={auctionStatusFilter}
          setValue={setAuctionStatusFilter}
          filterName="Event Status"
        />
        <TableFilter
          data={[
            { label: "Normal", value: "normal" },
            { label: "MarketDropAI", value: "marketDropAi" },
            { label: "ClaimPOINT", value: "claimPoint" }
          ]}
          value={typeFilter}
          setValue={setTypeFilter}
          filterName="Event Type"
        />
        <Input
          value={searchTerm}
          placeholder="Search events..."
          onChange={handleSearch}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => { setSearchTerm(""); fetchAuctionsData(); }}>
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
        <Button variant="contained" onClick={() => navigate("/event-management/add")}>
          Create Event
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <CardContent>
        <DataTable
          columns={columns}
          data={auctions}
          pagination
          paginationServer
          paginationTotalRows={totalCount}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          onChangeRowsPerPage={(currentRowsPerPage) => {
            setPageSize(currentRowsPerPage);
            setCurrentPage(1);
          }}
          progressPending={showLoader}
          progressComponent={<Loader />}
          paginationRowsPerPageOptions={[10, 20, 50]}
          paginationComponent={(props) => (
            <CustomPagination {...props} currentPage={currentPage} />
          )}
          customStyles={{
            headCells: {
              style: { fontWeight: "bold", fontSize: "14px" },
            },
          }}
        />
      </CardContent>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)}>
        <DialogTitle>Cancel Auction</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this auction? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)}>No</Button>
          <Button onClick={confirmCancel} color="warning" variant="contained">
            Yes, Cancel Auction
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Reason Input Dialog */}
      <Dialog 
        open={cancelReasonDialog} 
        onClose={() => {
          setCancelReasonDialog(false);
          setCancelReason("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            Cancel Auction - Reason Required
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for cancelling this auction. This information will be recorded and may be shared with bidders.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Cancellation Reason"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Enter the reason for cancelling this auction..."
            error={!cancelReason.trim() && cancelReason.length > 0}
            helperText={!cancelReason.trim() && cancelReason.length > 0 ? "Please provide a valid reason" : "Minimum 10 characters recommended"}
            sx={{ mt: 1 }}
          />
          {selectedAuction && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Auction Details:
              </Typography>
              <Typography variant="body2">
                <strong>Vehicle:</strong> {selectedAuction.vehicle?.vehicleName}
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {capitalize(selectedAuction.auctionType)}
              </Typography>
             
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setCancelReasonDialog(false);
              setCancelReason("");
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={submitCancelWithReason} 
            color="warning" 
            variant="contained"
            disabled={!cancelReason.trim()}
          >
            Submit Cancellation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Toggle Confirmation Dialog */}
      <Dialog
        open={statusDialog.open}
        onClose={() =>
          setStatusDialog({ open: false, id: "", currentStatus: 0 })
        }
      >
        <DialogTitle>
          Are you sure you want to{" "}
          {statusDialog.currentStatus === 1 ? "deactivate" : "activate"} this
          auction?
        </DialogTitle>
        <DialogActions>
          <Button variant="outlined" onClick={confirmStatusToggle} color="primary">
            Yes
          </Button>
          <Button
            variant="outlined"
            onClick={() =>
              setStatusDialog({ open: false, id: "", currentStatus: 0 })
            }
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default AuctionManagementList;